import {
  captionAlignmentToTextAlignment,
  CaptionAlignment,
  SectionDescriptionVisibility,
  Selection,
} from "@/lib/cms/section-types";
import { DimensionsFormatting } from "@/lib/compute-field/shared/dimensions";
import { InscriptionDosCF } from "@/lib/compute-field/shared/inscription-dos";
import { RelatedAuthorsListCF } from "@/lib/compute-field/shared/related-authors";
import { SignatureCF } from "@/lib/compute-field/shared/signature";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import { DateCF, MainTitleCF } from "@/lib/compute-field/shared/title";
import { SeparatedList } from "@/lib/compute-field/shared/utils";
import { AuthorCoreInfo, Dimensions } from "@/lib/types";
import { cn } from "@/lib/utils";
import {
  getMainLanguage,
  LocaleDict,
  SupportedLocale,
  supportedLocales,
} from "@/i18n";
import { useTranslation } from "react-i18next";

type SelectionCaptionProps = {
  item: Selection["item"];
  caption?: Selection["caption"];
  visibility: SectionDescriptionVisibility;
  captionAlignment: CaptionAlignment;
  className?: string;
};

export const SelectionCaption = ({
  item,
  caption,
  visibility,
  captionAlignment,
  className,
}: SelectionCaptionProps) => {
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <div
      className={cn("flex flex-col w-full py-2 gap-0.5", className)}
      style={{
        textAlign: captionAlignmentToTextAlignment(captionAlignment),
      }}
    >
      {item.imageType === "artwork" && (
        <SelectionInfoDisplay
          item={{
            title: item.oeuvresDescriptionTitre,
            unknownTitle: item.oeuvresDescriptionTitreInconnu,
            noTitle: item.oeuvresDescriptionTitreSansTitre,
            unconfirmedTitle: item.oeuvresValidationTitleNotConfirmed,
            authors: item.relatedAuthors,
            date: item.oeuvresDescriptionDateDeCreation,
            dateUnknown: item.oeuvresDescriptionDateDeCreationInconnue,
            dateUncertain: item.oeuvresDescriptionDateDeCreationIncertaine,
            technique: item.oeuvresDescriptionTechnique,
            support: item.oeuvresDescriptionSupport,
            doubleFaced: item.oeuvresDescriptionOeuvreDoubleFace,
            dimensions: item.oeuvresDescriptionDimensionsOeuvre,
            dimensionsUnknown: item.oeuvresDescriptionDimensionsOeuvreInconnues,
            backText: item.oeuvresDescriptionInscriptionsDosMultilingual,
            signatureLocalisation: item.oeuvresDescriptionSignatureLocalisation,
            signatureTranscription: item.oeuvresDescriptionSignatureTranscription,
            registrationNumber: item.oeuvresDescriptionNumeroEnregistrement,
            collection: item.oeuvresLocalisationHistoriqueCollectionCatalogue,
          }}
          visibility={visibility}
        />
      )}

      {item.imageType === "multipleprint" && (
        <SelectionInfoDisplay
          item={{
            title: item.estampesMultiplesDescriptionTitre,
            unknownTitle: item.estampesMultiplesDescriptionTitreInconnu,
            noTitle: item.estampesMultiplesDescriptionTitreSansTitre,
            unconfirmedTitle: item.estampesMultiplesDescriptionTitreIncertin,
            authors: item.relatedAuthors,
            date: item.estampesMultiplesDescriptionDateDeCreation,
            dateUnknown: item.estampesMultiplesDescriptionDateDeCreationInconnue,
            dateUncertain:
              item.estampesMultiplesDescriptionDateDeCreationIncertaine,
            technique: item.estampesMultiplesDescriptionTechnique,
            support: item.estampesMultiplesDescriptionSupportMateriau,
            doubleFaced: item.estampesMultiplesDescriptionOeuvreDoubleFace,
            dimensions: item.estampesMultiplesDescriptionDimensionsOeuvre,
            dimensionsUnknown:
              item.estampesMultiplesDescriptionDimensionsOeuvreInconnues,
            backText: item.estampesMultiplesDescriptionInscriptionsDos,
            signatureLocalisation:
              item.estampesMultiplesDescriptionSignatureLocalisation,
            signatureTranscription:
              item.estampesMultiplesDescriptionSignatureTranscription,
            registrationNumber:
              item.estampesMultiplesDescriptionNumeroEnregistrement,
          }}
          visibility={visibility}
        />
      )}

      {visibility.visibleCustomField &&
        caption?.[language as SupportedLocale]?.content && (
          <p
            className="text-xs pt-3"
            dangerouslySetInnerHTML={{
              __html: caption?.[getMainLanguage()]?.content ?? "",
            }}
          />
        )}
    </div>
  );
};

type SelectionInfoDisplayProps = {
  item: {
    title: string;
    unknownTitle: boolean;
    noTitle: boolean;
    unconfirmedTitle: boolean;
    authors: AuthorCoreInfo[];
    date: string;
    dateUnknown: boolean;
    dateUncertain?: boolean;
    technique: LocaleDict<string>;
    support: LocaleDict<string>;
    doubleFaced: boolean;
    dimensions: Dimensions;
    dimensionsUnknown: boolean;
    backText: LocaleDict<string>;
    signatureLocalisation: LocaleDict<string>;
    signatureTranscription: string;
    registrationNumber: string;
    collection?: string;
  };
  visibility: SectionDescriptionVisibility;
};

const SelectionInfoDisplay = ({
  item,
  visibility,
}: SelectionInfoDisplayProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  return (
    <>
      {visibility.visibleRegisterNumber && item.registrationNumber && (
        <p className="text-xs pb-3">
          <span className="cms-page-text-muted">
            {t("registrationNumber")}
            {" : "}
          </span>
          <span className="font-semibold">
            {item.registrationNumber}
          </span>
        </p>
      )}

      {visibility.visibleAuthors && item.authors?.[0] && (
        <p className="text-xs">
          <RelatedAuthorsListCF
            input={{
              relatedAuthors: item.authors,
              attributedTo: false,
              from: false,
              tasteOf: false,
              entourageOf: false,
              studioOf: false,
            }}
            language={language as SupportedLocale}
          />
        </p>
      )}

      {(visibility.visibleTitles || visibility.visibleDates) &&
        (item.title || item.date || item.dateUnknown) && (
          <p>
            <SeparatedList
              language={language as SupportedLocale}
              commaSeparated
              lineBreak={false}
              input={[
                visibility.visibleTitles && (
                  <b>
                    <MainTitleCF
                      input={{
                        title: item.title,
                        titleState: {
                          notConfirmed: item.unconfirmedTitle,
                          none: item.noTitle,
                          unknown: item.unknownTitle,
                        },
                      }}
                      language={language as SupportedLocale}
                    />
                  </b>
                ),
                visibility.visibleDates && (
                  <DateCF
                    input={{
                      date: item.date,
                      unknownDate: item.dateUnknown,
                      uncertainDate: item.dateUncertain,
                    }}
                    language={language as SupportedLocale}
                  />
                ),
              ]}
            />
          </p>
        )}

      {visibility.visibleDimensions && item.dimensions && (
        <p className="text-xs">
          <DimensionsFormatting
            input={{
              ...item.dimensions,
              unknownDimensions: item.dimensionsUnknown,
            }}
            language={language as SupportedLocale}
          />
        </p>
      )}

      {visibility.visibleTechniqueAndSupport &&
        (item.support?.[language as SupportedLocale] ||
          item.technique?.[language as SupportedLocale] ||
          item.doubleFaced) && (
          <p className="text-xs">
            <SupportTechniqueCF
              input={{
                technique: item.technique,
                support: item.support,
                isDoubleSided: item.doubleFaced,
              }}
              language={language as SupportedLocale}
            />
          </p>
        )}

      {visibility.visibleSignature &&
        (item.signatureLocalisation?.[language as SupportedLocale] ||
          item.signatureTranscription) && (
          <p className="text-xs">
            <SignatureCF
              input={{
                location: item.signatureLocalisation,
                transcription: item.signatureTranscription,
              }}
              language={language as SupportedLocale}
            />
          </p>
        )}

      {visibility.visibleBackText &&
        item.backText?.[language as SupportedLocale] && (
          <p className="text-xs">
            <InscriptionDosCF
              input={{
                inscription: item.backText,
                published: supportedLocales.reduce((acc, locale) => {
                  acc[locale] = true;
                  return acc;
                }, {} as LocaleDict<boolean>),
              }}
              language={language as SupportedLocale}
            />
          </p>
        )}

      {visibility.visibleCurrentCollection && item.collection && (
        <p className="text-xs">{item.collection}</p>
      )}
    </>
  );
};
